<template>
  <div>
    <div
      v-if="placeholderStyles"
      :style="placeholderStyles"
    />
    <div
      class="ten-affix"
      ref="child"
      :style="styles"
      :class="className"
    >
      <!-- @slot 固定的内容 -->
      <slot />
    </div>
  </div>
</template>

<script>
import isUndefined from 'lodash/isUndefined';
import { affixStatus, isSuperIntersectionObserver, isSuperResizeObserver, getRect } from './util';

const name = 'ten-affix';

export default {
  name,

  props: {
    /**
     * 监听滚动的元素(默认为window)，传入选择器字符串如'#parent'
     */
    target: String,

    /**
     * 距离窗口顶部达到指定偏移量后触发
     */
    offsetTop: {
      type: Number,
      default: 0,
    },

    /**
     * 距离窗口底部达到指定偏移量后触发
     */
    offsetBottom: {
      type: Number,
    },

    /**
     * 自定义className
     */
    className: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      styles: null,
      placeholderStyles: null,
      lastStatus: affixStatus.none,
      shouldCompute: true,
    };
  },

  mounted() {
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.targetEl = window;

    this.init();
    this.targetEl.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    this.removeResizeListener();
    this.removeInterListener();
    this.targetEl.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    init() {
      if (this.target) {
        this.targetEl = document.querySelector(this.target);
      }

      this.addResizeListener();
      this.addInterListener();
    },

    addResizeListener() {
      if (isSuperResizeObserver()) {
        this.resizeObserver = new ResizeObserver(this.updatePosition);
        this.resizeObserver.observe(document.documentElement);
      } else {
        window.addEventListener('resize', this.updatePosition);
      }
    },

    removeResizeListener() {
      if (isSuperResizeObserver()) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.updatePosition);
      }
    },

    addInterListener() {
      if (isSuperIntersectionObserver()) {
        this.intersectionObserver = new IntersectionObserver((inters) => {
          const [inter] = inters;
          this.shouldCompute = inter.isIntersecting;
          this.shouldCompute && this.updatePosition();
        });
        this.intersectionObserver.observe(this.$el);
      }
    },

    removeInterListener() {
      if (isSuperIntersectionObserver()) {
        this.intersectionObserver.disconnect();
      }
    },

    getFixedPos() {
      const elRect = getRect(this.$el);
      const targetElRect = getRect(this.targetEl);
      const hasOffsetTop = 'offsetTop' in this.$vnode.componentOptions.propsData;
      const hasOffsetBottom = 'offsetBottom' in this.$vnode.componentOptions.propsData;

      let fixedTop;
      let fixedBottom;

      if (!hasOffsetTop && hasOffsetBottom) {
        if (targetElRect.bottom - elRect.bottom <= this.offsetBottom) {
          fixedBottom = window.innerHeight - targetElRect.bottom + this.offsetBottom;
        }
      } else {
        if (elRect.top - targetElRect.top <= this.offsetTop) {
          fixedTop = this.offsetTop + targetElRect.top;
        }
      }

      return [fixedTop, fixedBottom];
    },

    updatePosition() {
      const [fixedTop, fixedBottom] = this.getFixedPos();
      const force = !!this.target;
      const isNone = isUndefined(fixedTop) && isUndefined(fixedBottom);

      if (!force) {
        if (
          ((this.lastStatus === affixStatus.none) && isNone)
          || ((this.lastStatus === affixStatus.fixed) && !isNone)
        ) return;
      }

      /**
       * fixed状态变更时的回调
       *
       * @param {Boolean} 当前是否fixed
       */
      this.$emit('change', !isNone);

      if (isNone) {
        this.lastStatus = affixStatus.none;
        this.styles = null;
        this.placeholderStyles = null;
      } else {
        this.lastStatus = affixStatus.fixed;
        const { width, height, left } = getRect(this.$refs.child);
        const nextStyles = {
          zIndex: 10,
          width: `${width}px`,
          height: `${height}px`,
          left: `${left}px`,
          position: 'fixed',
        };

        this.placeholderStyles = {
          width: `${width}px`,
          height: `${height}px`,
        };

        if (!isUndefined(fixedTop)) {
          this.styles = {
            ...nextStyles,
            top: `${fixedTop}px`,
          };
        }
        if (!isUndefined(fixedBottom)) {
          this.styles = {
            ...nextStyles,
            bottom: `${fixedBottom}px`,
          };
        }
      }
    },

    handleScroll() {
      this.shouldCompute && this.updatePosition();
    },
  },

};
</script>
